const isH2 =
  (window.performance?.getEntriesByType("navigation")?.[0] as PerformanceResourceTiming)?.nextHopProtocol === "h2";
const TOTAL_PARALLEL_CALLS = isH2 ? 20 : 5;

export enum ApiCallPriority {
  LOW,
  NORMAL,
  HIGH,
}

interface CallConfig {
  priority: ApiCallPriority;
  skipParallelLimit?: boolean;
}

type QueuedCall = () => Promise<Response>;

const queues: Record<ApiCallPriority, QueuedCall[]> = {
  [ApiCallPriority.LOW]: [],
  [ApiCallPriority.NORMAL]: [],
  [ApiCallPriority.HIGH]: [],
};

let ongoingCalls: Promise<Response>[] = [];

const DEFAULT_CONFIG: RequestInit = {
  credentials: "same-origin", // include, omit
  mode: "cors", // no-cors, same-origin
  referrer: "no-referrer", // client
};

export function apiGET(
  url: string,
  init?: Omit<RequestInit, "method">,
  priority: ApiCallPriority | CallConfig = ApiCallPriority.NORMAL,
) {
  const config: RequestInit = {
    ...DEFAULT_CONFIG,
    ...(init ?? {}),
    method: "get",
  };

  let callConfig;
  if (typeof priority === "number") {
    callConfig = { priority };
  } else {
    callConfig = priority;
  }

  return scheduleNewCall(() => fetch(url, config), callConfig);
}

export function apiPOST(
  url: string,
  init?: Omit<RequestInit, "method">,
  priority: ApiCallPriority | CallConfig = ApiCallPriority.NORMAL,
) {
  const config: RequestInit = {
    ...DEFAULT_CONFIG,
    ...(init ?? {}),
    method: "post",
  };

  let callConfig;
  if (typeof priority === "number") {
    callConfig = { priority };
  } else {
    callConfig = priority;
  }

  return scheduleNewCall(() => fetch(url, config), callConfig);
}

function scheduleNewCall(fetchingPromise: QueuedCall, config: CallConfig) {
  let promise: Promise<Response>;
  if (ongoingCalls.length < TOTAL_PARALLEL_CALLS || config.skipParallelLimit) {
    promise = fetchingPromise();
    promise.finally(() => {
      ongoingCalls = ongoingCalls.filter((item) => item !== promise);
      scheduleCallFromQueue();
    });
    ongoingCalls.push(promise);
  } else {
    promise = new Promise<Response>((resolve, reject) => {
      const queueElement = () => {
        const promise = fetchingPromise();
        promise.then(resolve, reject);
        return promise;
      };
      queues[config.priority].push(queueElement);
    });
  }

  return promise;
}

function scheduleCallFromQueue() {
  while (ongoingCalls.length < TOTAL_PARALLEL_CALLS) {
    const queueElement =
      queues[ApiCallPriority.HIGH].shift() ??
      queues[ApiCallPriority.NORMAL].shift() ??
      queues[ApiCallPriority.LOW].shift();
    if (!queueElement) {
      break;
    }

    const promise = queueElement();
    promise.finally(() => {
      ongoingCalls = ongoingCalls.filter((item) => item !== promise);
      scheduleCallFromQueue();
    });
    ongoingCalls.push(promise);
  }
}
